import { createContext, defineEventa } from "@moeru/eventa";
import { readGraphemeClusters } from "clustr";
//#region src/eventa.ts
const speechSegmentEvent = defineEventa("proj-airi:pipelines:output:speech:segment");
const speechSpecialEvent = defineEventa("proj-airi:pipelines:output:speech:special");
const speechTtsRequestEvent = defineEventa("proj-airi:pipelines:output:speech:tts-request");
const speechTtsResultEvent = defineEventa("proj-airi:pipelines:output:speech:tts-result");
const speechPlaybackStartEvent = defineEventa("proj-airi:pipelines:output:speech:playback-start");
const speechPlaybackEndEvent = defineEventa("proj-airi:pipelines:output:speech:playback-end");
const speechPlaybackInterruptEvent = defineEventa("proj-airi:pipelines:output:speech:playback-interrupt");
const speechPlaybackRejectEvent = defineEventa("proj-airi:pipelines:output:speech:playback-reject");
const speechIntentStartEvent = defineEventa("proj-airi:pipelines:output:speech:intent-start");
const speechIntentEndEvent = defineEventa("proj-airi:pipelines:output:speech:intent-end");
const speechIntentCancelEvent = defineEventa("proj-airi:pipelines:output:speech:intent-cancel");
const speechTurnStartEvent = defineEventa("proj-airi:pipelines:output:speech:turn-start");
const speechTurnEndEvent = defineEventa("proj-airi:pipelines:output:speech:turn-end");
const speechTurnCancelEvent = defineEventa("proj-airi:pipelines:output:speech:turn-cancel");
const speechPipelineEventMap = {
	onSegment: speechSegmentEvent,
	onSpecial: speechSpecialEvent,
	onTtsRequest: speechTtsRequestEvent,
	onTtsResult: speechTtsResultEvent,
	onPlaybackStart: speechPlaybackStartEvent,
	onPlaybackEnd: speechPlaybackEndEvent,
	onPlaybackInterrupt: speechPlaybackInterruptEvent,
	onPlaybackReject: speechPlaybackRejectEvent,
	onIntentStart: speechIntentStartEvent,
	onIntentEnd: speechIntentEndEvent,
	onIntentCancel: speechIntentCancelEvent,
	onTurnStart: speechTurnStartEvent,
	onTurnEnd: speechTurnEndEvent,
	onTurnCancel: speechTurnCancelEvent
};
//#endregion
//#region src/llm-streaming-control/parsers/act.ts
const actTokenPrefix = "<|ACT ";
const markerSuffix$2 = "|>";
function isPlainObject$1(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
* Creates the parser for `<|ACT {...}|>` streaming-control tokens.
*
* Use when:
* - Loading the built-in performance action control
*
* Expects:
* - The token body is a JSON object literal
*
* Returns:
* - Parsed action data with no side effects
*/
function tokenAct() {
	return {
		name: "ACT",
		match(special) {
			const trimmed = special.trim();
			return trimmed.startsWith(actTokenPrefix) && trimmed.endsWith(markerSuffix$2);
		},
		parse(special) {
			const rawPayload = special.trim().slice(6, -2).trim();
			let parsed;
			try {
				parsed = JSON.parse(rawPayload);
			} catch {
				return;
			}
			if (!isPlainObject$1(parsed)) return;
			return {
				type: "act",
				payload: parsed
			};
		}
	};
}
//#endregion
//#region src/llm-streaming-control/parsers/call.ts
const callTokenPrefix = "<|CALL ";
const markerSuffix$1 = "|>";
function isPlainObject(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
function renderCallManifestExamples(manifest) {
	if (manifest.examples?.length) return manifest.examples;
	return [`<|CALL ["${manifest.name}"]|>`];
}
/**
* Renders model-facing instructions for registered `<|CALL [...]|>` manifests.
*
* Use when:
* - Injecting currently available CALL token affordances into a model prompt
*
* Expects:
* - Manifests are already normalized by the streaming-control registry
*
* Returns:
* - Empty string when no manifests are registered
* - A CALL-specific instruction block with syntax rules and examples otherwise
*/
function renderCallManifestPrompt(manifests) {
	if (manifests.length === 0) return "";
	const lines = [
		"Available streaming CALL tokens:",
		"Use these only as text special tokens when the matching plugin action is needed.",
		"Syntax: <|CALL [\"call.name\"]|> or <|CALL [\"call.name\", {\"key\":\"value\"}]|>.",
		"Never write provider tool names inside <|CALL ...|>.",
		"Never write JSON payload after the closing |>; all payload data belongs inside the JSON array.",
		""
	];
	for (const manifest of manifests) {
		lines.push(`- ${manifest.name}: ${manifest.prompt}`);
		lines.push("  Examples:");
		for (const example of renderCallManifestExamples(manifest)) lines.push(`  - ${example}`);
	}
	return lines.join("\n");
}
/**
* Creates the parser for `<|CALL [...]|>` streaming-control tokens.
*
* Use when:
* - Loading the built-in plugin callback control
*
* Expects:
* - The token body is a JSON array: `[name]` or `[name, payloadObject]`
*
* Returns:
* - Parsed call data with no side effects
*/
function tokenCall() {
	return {
		name: "CALL",
		match(special) {
			const trimmed = special.trim();
			return trimmed.startsWith(callTokenPrefix) && trimmed.endsWith(markerSuffix$1);
		},
		parse(special) {
			const rawPayload = special.trim().slice(7, -2).trim();
			let parsed;
			try {
				parsed = JSON.parse(rawPayload);
			} catch {
				return;
			}
			if (!Array.isArray(parsed) || parsed.length < 1 || parsed.length > 2) return;
			const [name, payload] = parsed;
			if (typeof name !== "string" || name.trim().length === 0) return;
			if (payload !== void 0 && !isPlainObject(payload)) return;
			return {
				type: "call",
				name: name.trim(),
				payload
			};
		}
	};
}
//#endregion
//#region src/llm-streaming-control/parsers/delay.ts
const delayTokenPrefix = "<|DELAY ";
const markerSuffix = "|>";
/**
* Creates the parser for `<|DELAY n|>` streaming-control tokens.
*
* Use when:
* - Loading the built-in performance delay control
*
* Expects:
* - The token body is a finite positive number literal in seconds
*
* Returns:
* - Parsed delay data with no side effects
*/
function tokenDelay() {
	return {
		name: "DELAY",
		match(special) {
			const trimmed = special.trim();
			return trimmed.startsWith(delayTokenPrefix) && trimmed.endsWith(markerSuffix);
		},
		parse(special) {
			const rawPayload = special.trim().slice(8, -2).trim();
			if (!/^\d+(?:\.\d+)?$/.test(rawPayload)) return;
			const seconds = Number.parseFloat(rawPayload);
			if (!Number.isFinite(seconds) || seconds <= 0) return;
			return {
				type: "delay",
				seconds
			};
		}
	};
}
//#endregion
//#region src/llm-streaming-control/controller.ts
function parsedParameter(signal) {
	switch (signal.type) {
		case "act": return JSON.stringify(signal.payload);
		case "call": return signal.payload ? JSON.stringify(signal.payload) : void 0;
		case "delay": return `${signal.seconds}s`;
	}
}
/**
* Creates a controller over LLM streaming-control tokens.
*
* Use when:
* - A stage runtime needs to dispatch special tokens from one playback source
* - A plugin bridge needs to register CALL callbacks against the same controller instance
*
* Expects:
* - The caller owns the controller lifetime, usually through a Pinia store
*
* Returns:
* - A controller with `match`, `dispatchWith`, and `on`
*/
function createStreamingControlParser(options = {}) {
	const handlers = /* @__PURE__ */ new Map();
	const callManifests = /* @__PURE__ */ new Map();
	const turns = /* @__PURE__ */ new Map();
	const signalHandlers = /* @__PURE__ */ new Set();
	const parsers = options.parsers ?? [
		tokenAct(),
		tokenDelay(),
		tokenCall()
	];
	return {
		match(special) {
			return parsers.some((parser) => parser.match(special));
		},
		async dispatchWith(special, context) {
			const parser = parsers.find((item) => item.match(special));
			if (!parser) {
				context?.observer?.({
					type: "rejected",
					reason: "no-matching-parser"
				});
				return false;
			}
			const parsed = parser.parse(special);
			if (!parsed) {
				context?.observer?.({
					type: "rejected",
					reason: "parse-failed",
					parserName: parser.name
				});
				return false;
			}
			context?.observer?.({
				type: "parsed",
				parserName: parser.name,
				tokenType: parsed.type,
				callName: parsed.type === "call" ? parsed.name : void 0,
				parameter: parsedParameter(parsed)
			});
			const { observer: _observer, ...dispatchContext } = context ?? {};
			const signalContext = {
				...dispatchContext,
				createdAt: Date.now()
			};
			for (const handler of signalHandlers) try {
				await handler(parsed, signalContext);
			} catch (error) {
				context?.observer?.({
					type: "signal-handler-error",
					tokenType: parsed.type,
					error
				});
				console.warn("[llm-streaming-control] signal handler failed", error);
			}
			if (parsed.type !== "call") return true;
			const turnHandlers = dispatchContext.turnId ? turns.get(dispatchContext.turnId)?.handlers.get(parsed.name) : void 0;
			const globalHandlers = handlers.get(parsed.name);
			const registeredHandlers = turnHandlers?.size ? [...turnHandlers] : [...globalHandlers ?? []];
			context?.observer?.({
				type: "call-handler-count",
				count: registeredHandlers.length
			});
			if (!registeredHandlers.length) {
				context?.observer?.({
					type: "call-handler-missing",
					callName: parsed.name,
					payload: parsed.payload
				});
				return true;
			}
			for (const handler of registeredHandlers) try {
				context?.observer?.({
					type: "call-handler-start",
					callName: parsed.name
				});
				await handler(parsed.payload, signalContext);
				context?.observer?.({
					type: "call-handler-end",
					callName: parsed.name
				});
			} catch (error) {
				context?.observer?.({
					type: "call-handler-error",
					callName: parsed.name,
					error
				});
				console.warn("[llm-streaming-control] handler failed", error);
			}
			return true;
		},
		on(manifest, handler) {
			const normalizedName = manifest.name.trim();
			const normalizedPrompt = manifest.prompt.trim();
			if (!normalizedName || !normalizedPrompt) return () => void 0;
			callManifests.set(normalizedName, {
				...manifest,
				name: normalizedName,
				prompt: normalizedPrompt
			});
			const registeredHandlers = handlers.get(normalizedName) ?? /* @__PURE__ */ new Set();
			registeredHandlers.add(handler);
			handlers.set(normalizedName, registeredHandlers);
			return () => {
				registeredHandlers.delete(handler);
				if (registeredHandlers.size === 0) {
					handlers.delete(normalizedName);
					callManifests.delete(normalizedName);
				}
			};
		},
		renderManifestPrompt() {
			return renderCallManifestPrompt([...callManifests.values()]);
		},
		onSignal(handler) {
			signalHandlers.add(handler);
			return () => {
				signalHandlers.delete(handler);
			};
		},
		beginTurn(options) {
			const turnId = options?.turnId?.trim() || `turn:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
			const existing = turns.get(turnId);
			if (existing) return {
				turnId,
				on(manifest, handler) {
					return registerTurnHandler(existing, manifest, handler);
				},
				renderManifestPrompt() {
					return renderCallManifestPrompt([...existing.callManifests.values()]);
				},
				complete() {
					existing.settle({ type: "completed" });
					turns.delete(turnId);
				},
				cancel() {
					existing.settle({ type: "cancelled" });
					turns.delete(turnId);
				},
				done: existing.done
			};
			let settle;
			let settled = false;
			const done = new Promise((resolve) => {
				settle = (result) => {
					if (settled) return;
					settled = true;
					resolve(result);
				};
			});
			const turn = {
				handlers: /* @__PURE__ */ new Map(),
				callManifests: /* @__PURE__ */ new Map(),
				settle,
				done
			};
			turns.set(turnId, turn);
			return {
				turnId,
				on(manifest, handler) {
					return registerTurnHandler(turn, manifest, handler);
				},
				renderManifestPrompt() {
					return renderCallManifestPrompt([...turn.callManifests.values()]);
				},
				complete() {
					settle({ type: "completed" });
					turns.delete(turnId);
				},
				cancel() {
					settle({ type: "cancelled" });
					turns.delete(turnId);
				},
				done
			};
		},
		completeTurn(turnId) {
			const turn = turns.get(turnId);
			if (!turn) return;
			turn.settle({ type: "completed" });
			turns.delete(turnId);
		},
		cancelTurn(turnId) {
			const turn = turns.get(turnId);
			if (!turn) return;
			turn.settle({ type: "cancelled" });
			turns.delete(turnId);
		}
	};
	function registerTurnHandler(turn, manifest, handler) {
		const normalizedName = manifest.name.trim();
		const normalizedPrompt = manifest.prompt.trim();
		if (!normalizedName || !normalizedPrompt) return () => void 0;
		turn.callManifests.set(normalizedName, {
			...manifest,
			name: normalizedName,
			prompt: normalizedPrompt
		});
		const registeredHandlers = turn.handlers.get(normalizedName) ?? /* @__PURE__ */ new Set();
		registeredHandlers.add(handler);
		turn.handlers.set(normalizedName, registeredHandlers);
		return () => {
			registeredHandlers.delete(handler);
			if (registeredHandlers.size === 0) {
				turn.handlers.delete(normalizedName);
				turn.callManifests.delete(normalizedName);
			}
		};
	}
}
//#endregion
//#region src/llm-streaming-control/payloads.ts
const emotionValues = [
	"happy",
	"sad",
	"angry",
	"think",
	"surprised",
	"awkward",
	"question",
	"curious",
	"neutral"
];
function normalizeEmotionName(value) {
	const normalized = value.trim().toLowerCase();
	if (emotionValues.includes(normalized)) return normalized;
}
function normalizeIntensity(value) {
	if (typeof value !== "number" || Number.isNaN(value)) return 1;
	return Math.min(1, Math.max(0, value));
}
function normalizeEmotion(value) {
	if (typeof value === "string") {
		const name = normalizeEmotionName(value);
		return name ? {
			name,
			intensity: 1
		} : void 0;
	}
	if (!value || typeof value !== "object" || Array.isArray(value)) return;
	if (!("name" in value) || typeof value.name !== "string") return;
	const name = normalizeEmotionName(value.name);
	if (!name) return;
	return {
		name,
		intensity: normalizeIntensity("intensity" in value ? value.intensity : void 0)
	};
}
function normalizeMotion(value) {
	if (typeof value !== "string") return;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : void 0;
}
/**
* Normalizes ACT token payloads.
*
* Before:
* - `{ emotion: "Surprised", motion: " nod " }`
*
* After:
* - `{ emotion: { name: "surprised", intensity: 1 }, motion: "nod" }`
*/
function normalizeActPayload(payload) {
	const normalized = {};
	const emotion = normalizeEmotion(payload.emotion);
	const motion = normalizeMotion(payload.motion);
	if (emotion) normalized.emotion = emotion;
	if (motion) normalized.motion = motion;
	return normalized;
}
//#endregion
//#region src/managers/playback-manager.ts
function createPlaybackManager(options) {
	const maxVoices = Math.max(1, options.maxVoices ?? 1);
	const maxVoicesPerOwner = options.maxVoicesPerOwner;
	const overflowPolicy = options.overflowPolicy ?? "queue";
	const ownerOverflowPolicy = options.ownerOverflowPolicy ?? "steal-oldest";
	const active = /* @__PURE__ */ new Map();
	const waiting = [];
	const listeners = {
		start: [],
		end: [],
		interrupt: [],
		reject: []
	};
	function onStart(listener) {
		listeners.start.push(listener);
	}
	function onEnd(listener) {
		listeners.end.push(listener);
	}
	function onInterrupt(listener) {
		listeners.interrupt.push(listener);
	}
	function onReject(listener) {
		listeners.reject.push(listener);
	}
	function emitStart(item) {
		const event = {
			item,
			startedAt: Date.now()
		};
		listeners.start.forEach((listener) => listener(event));
	}
	function emitEnd(item) {
		const event = {
			item,
			endedAt: Date.now()
		};
		listeners.end.forEach((listener) => listener(event));
	}
	function emitInterrupt(item, reason) {
		const event = {
			item,
			reason,
			interruptedAt: Date.now()
		};
		listeners.interrupt.forEach((listener) => listener(event));
	}
	function emitReject(item, reason) {
		const event = {
			item,
			reason
		};
		listeners.reject.forEach((listener) => listener(event));
	}
	function countByOwner(ownerId) {
		if (!ownerId) return 0;
		let count = 0;
		for (const entry of active.values()) if (entry.item.ownerId === ownerId) count += 1;
		return count;
	}
	function chooseVictimByPriority() {
		let victim;
		for (const entry of active.values()) if (!victim) victim = entry;
		else if (entry.item.priority < victim.item.priority) victim = entry;
		return victim;
	}
	function chooseVictimOldest(ownerId) {
		let victim;
		for (const entry of active.values()) {
			if (ownerId && entry.item.ownerId !== ownerId) continue;
			if (!victim || entry.startedAt < victim.startedAt) victim = entry;
		}
		return victim;
	}
	function stopActive(entry, reason) {
		entry.controller.abort(reason);
		active.delete(entry.item.id);
		emitInterrupt(entry.item, reason);
	}
	function canStart(item) {
		if (active.size >= maxVoices) return {
			ok: false,
			reason: "overflow"
		};
		if (maxVoicesPerOwner && item.ownerId) {
			if (countByOwner(item.ownerId) >= maxVoicesPerOwner) return {
				ok: false,
				reason: "owner-overflow"
			};
		}
		return { ok: true };
	}
	function start(item) {
		const controller = new AbortController();
		const startedAt = Date.now();
		active.set(item.id, {
			item,
			controller,
			startedAt
		});
		emitStart(item);
		options.play(item, controller.signal).then(() => {
			if (!active.has(item.id)) return;
			active.delete(item.id);
			emitEnd(item);
			tryStartWaiting();
		}).catch((err) => {
			if (!active.has(item.id)) return;
			active.delete(item.id);
			emitInterrupt(item, err instanceof Error ? err.message : "playback-error");
			tryStartWaiting();
		});
	}
	function tryStartWaiting() {
		if (waiting.length === 0) return;
		const candidates = waiting.slice().sort((a, b) => b.item.priority - a.item.priority || a.enqueuedAt - b.enqueuedAt);
		for (const candidate of candidates) {
			const { ok, reason } = canStart(candidate.item);
			if (!ok) {
				if (reason === "owner-overflow" && ownerOverflowPolicy === "steal-oldest") {
					const victim = chooseVictimOldest(candidate.item.ownerId);
					if (victim) stopActive(victim, "owner-overflow");
				}
				continue;
			}
			const index = waiting.indexOf(candidate);
			if (index >= 0) waiting.splice(index, 1);
			start(candidate.item);
			if (active.size >= maxVoices) break;
		}
	}
	function handleOverflow(item, reason) {
		if (reason === "owner-overflow") {
			if (ownerOverflowPolicy === "reject") {
				emitReject(item, "owner-overflow");
				return;
			}
			const victim = chooseVictimOldest(item.ownerId);
			if (victim) {
				stopActive(victim, "owner-overflow");
				waiting.push({
					item,
					enqueuedAt: Date.now()
				});
				tryStartWaiting();
				return;
			}
		}
		switch (overflowPolicy) {
			case "reject":
				emitReject(item, "overflow");
				break;
			case "queue":
				waiting.push({
					item,
					enqueuedAt: Date.now()
				});
				break;
			case "steal-oldest": {
				const victim = chooseVictimOldest();
				if (victim) stopActive(victim, "steal-oldest");
				waiting.push({
					item,
					enqueuedAt: Date.now()
				});
				tryStartWaiting();
				break;
			}
			case "steal-lowest-priority": {
				const victim = chooseVictimByPriority();
				if (victim && victim.item.priority <= item.priority) {
					stopActive(victim, "steal-lowest-priority");
					waiting.push({
						item,
						enqueuedAt: Date.now()
					});
					tryStartWaiting();
				} else emitReject(item, "lower-priority");
				break;
			}
		}
	}
	function schedule(item) {
		const { ok, reason } = canStart(item);
		if (ok) {
			start(item);
			return;
		}
		handleOverflow(item, reason);
	}
	function stopAll(reason) {
		for (const entry of active.values()) stopActive(entry, reason);
		waiting.length = 0;
	}
	function stopByIntent(intentId, reason) {
		for (const entry of active.values()) {
			if (entry.item.intentId !== intentId) continue;
			stopActive(entry, reason);
		}
		for (let i = waiting.length - 1; i >= 0; i -= 1) if (waiting[i]?.item.intentId === intentId) waiting.splice(i, 1);
	}
	function stopByOwner(ownerId, reason) {
		for (const entry of active.values()) {
			if (entry.item.ownerId !== ownerId) continue;
			stopActive(entry, reason);
		}
		for (let i = waiting.length - 1; i >= 0; i -= 1) if (waiting[i]?.item.ownerId === ownerId) waiting.splice(i, 1);
	}
	return {
		schedule,
		stopAll,
		stopByIntent,
		stopByOwner,
		onStart,
		onEnd,
		onInterrupt,
		onReject
	};
}
//#endregion
//#region src/priority.ts
const DEFAULT_LEVELS = {
	critical: 300,
	high: 200,
	normal: 100,
	low: 0
};
function createPriorityResolver(levels) {
	const resolved = {
		...DEFAULT_LEVELS,
		...levels
	};
	return { resolve(priority) {
		if (priority == null) return resolved.normal;
		if (typeof priority === "number") return priority;
		return resolved[priority] ?? resolved.normal;
	} };
}
function comparePriority(a, b) {
	if (a === b) return 0;
	return a > b ? 1 : -1;
}
//#endregion
//#region src/stream.ts
function createPushStream() {
	let closed = false;
	let controller = null;
	return {
		stream: new ReadableStream({
			start(ctrl) {
				controller = ctrl;
			},
			cancel() {
				closed = true;
			}
		}),
		write(value) {
			if (!controller || closed) return;
			controller.enqueue(value);
		},
		close() {
			if (!controller || closed) return;
			closed = true;
			controller.close();
		},
		error(err) {
			if (!controller || closed) return;
			closed = true;
			controller.error(err);
		},
		isClosed() {
			return closed;
		}
	};
}
async function readStream(stream, handler) {
	const reader = stream.getReader();
	try {
		while (true) {
			const { value, done } = await reader.read();
			if (done) break;
			await handler(value);
		}
	} finally {
		reader.releaseLock();
	}
}
//#endregion
//#region src/processors/tts-chunker.ts
const TTS_FLUSH_INSTRUCTION = "​";
const TTS_SPECIAL_TOKEN = "⁣";
const regexpAnySingleDigit = /\d/;
const keptPunctuations = /* @__PURE__ */ new Set("?？!！");
const hardPunctuations = /* @__PURE__ */ new Set(".。?？!！…⋯～~\n	\r");
const softPunctuations = /* @__PURE__ */ new Set(",，、–—:：;；《》「」");
async function* chunkTtsInput(input, options) {
	const { boost = 2, minimumWords = 4, maximumWords = 12 } = options ?? {};
	const iterator = readGraphemeClusters(typeof input === "string" ? new ReadableStream({ start(controller) {
		controller.enqueue(new TextEncoder().encode(input));
		controller.close();
	} }).getReader() : input);
	const segmenter = new Intl.Segmenter(void 0, { granularity: "word" });
	let yieldCount = 0;
	let buffer = "";
	let chunk = "";
	let chunkWordsCount = 0;
	let previousValue;
	let current = await iterator.next();
	while (!current.done) {
		let value = current.value;
		if (value.length > 1) {
			previousValue = value;
			current = await iterator.next();
			continue;
		}
		const flush = value === "​";
		const special = value === "⁣";
		const hard = hardPunctuations.has(value);
		const soft = softPunctuations.has(value);
		const kept = keptPunctuations.has(value);
		let next;
		let afterNext;
		if (flush || special || hard || soft) {
			switch (value) {
				case ".":
				case ",": if (previousValue !== void 0 && regexpAnySingleDigit.test(previousValue)) {
					next = await iterator.next();
					if (!next.done && next.value && regexpAnySingleDigit.test(next.value)) {
						buffer += value;
						current = next;
						next = void 0;
						continue;
					}
				} else if (value === ".") {
					next = await iterator.next();
					if (!next.done && next.value && next.value === ".") {
						afterNext = await iterator.next();
						if (!afterNext.done && afterNext.value && afterNext.value === ".") {
							value = "…";
							next = void 0;
							afterNext = void 0;
						}
					}
				}
			}
			if (buffer.length === 0) {
				if (special) {
					yield {
						text: "",
						words: 0,
						reason: "special"
					};
					yieldCount++;
					chunkWordsCount = 0;
				}
				previousValue = value;
				current = await iterator.next();
				continue;
			}
			const words = [...segmenter.segment(buffer)].filter((w) => w.isWordLike);
			if (chunkWordsCount > minimumWords && chunkWordsCount + words.length > maximumWords) {
				yield {
					text: kept ? chunk.trim() + value : chunk.trim(),
					words: chunkWordsCount,
					reason: "limit"
				};
				yieldCount++;
				chunk = "";
				chunkWordsCount = 0;
			}
			chunk += buffer + value;
			chunkWordsCount += words.length;
			buffer = "";
			if (special) {
				yield {
					text: chunk.slice(0, -1).trim(),
					words: chunkWordsCount,
					reason: "special"
				};
				yieldCount++;
				chunk = "";
				chunkWordsCount = 0;
			} else if (flush || hard || chunkWordsCount > maximumWords || yieldCount < boost) {
				yield {
					text: chunk.trim(),
					words: chunkWordsCount,
					reason: flush ? "flush" : hard ? "hard" : chunkWordsCount > maximumWords ? "limit" : "boost"
				};
				yieldCount++;
				chunk = "";
				chunkWordsCount = 0;
			}
			previousValue = value;
			if (next !== void 0) if (afterNext !== void 0) {
				current = afterNext;
				next = void 0;
				afterNext = void 0;
			} else {
				current = next;
				next = void 0;
			}
			else current = await iterator.next();
			continue;
		}
		buffer += value;
		previousValue = value;
		next = await iterator.next();
		current = next;
	}
	console.debug("while loop ends, chunk/buffer:", chunk, buffer);
	if (chunk.length > 0 || buffer.length > 0) yield {
		text: (chunk + buffer).trim(),
		words: chunkWordsCount + [...segmenter.segment(buffer)].filter((w) => w.isWordLike).length,
		reason: "flush"
	};
}
async function chunkEmitter(reader, pendingSpecials, options, handler) {
	const sanitizeChunk = (text) => {
		return text.replaceAll("⁣", "").replaceAll("​", "").trim();
	};
	try {
		for await (const chunk of chunkTtsInput(reader, options)) {
			const cleanedText = sanitizeChunk(chunk.text);
			if (!cleanedText && chunk.reason !== "special") continue;
			if (chunk.reason === "special") await handler({
				chunk: cleanedText,
				special: pendingSpecials.shift() ?? null,
				reason: chunk.reason
			});
			else await handler({
				chunk: cleanedText,
				special: null,
				reason: chunk.reason
			});
		}
	} catch (e) {
		console.error("Error chunking stream to TTS queue:", e);
	}
}
const BRACKET_MAP = {
	"[": "]",
	"(": ")",
	"（": "）",
	"【": "】",
	"<": ">"
};
const OPENERS = Object.keys(BRACKET_MAP);
const CLOSERS = Object.values(BRACKET_MAP);
const isUnicodeLetter = (char) => /\p{L}/u.test(char);
const NARRATIVE_KEYWORDS = [
	"laugh",
	"sigh",
	"action",
	"note",
	"breath",
	"giggle",
	"whisper",
	"cry",
	"smile",
	"thought"
];
function isProbablyAngleTag(index, text) {
	if (text[index] !== "<") return false;
	if (text[index + 1] === "/") return true;
	const remainder = text.slice(index + 1).toLowerCase();
	const nextChar = remainder[0];
	const prevChar = index > 0 ? text[index - 1] : "";
	if (nextChar === "/") return true;
	if (nextChar && /[0-9\s=]/.test(nextChar)) return false;
	if (prevChar && (isUnicodeLetter(prevChar) || /\d/.test(prevChar))) return NARRATIVE_KEYWORDS.some((kw) => remainder.length > 1 && kw.startsWith(remainder) || remainder.startsWith(kw));
	if (prevChar && /[^\s([{（【<\])}>）】.,!?;:，。！？；：'"\-_]/.test(prevChar)) return false;
	return true;
}
function processNarrative(text, options) {
	if (!options?.stripNarrative) return text;
	const rangesToRemove = [];
	const charsToRemove = /* @__PURE__ */ new Set();
	const stack = [];
	let starOpenIndex = -1;
	for (let i = 0; i < text.length; i++) {
		const char = text[i];
		if (char === "*") {
			if (starOpenIndex !== -1) {
				if (options?.keepNarrativeText) {
					charsToRemove.add(starOpenIndex);
					charsToRemove.add(i);
				} else rangesToRemove.push([starOpenIndex, i]);
				starOpenIndex = -1;
			} else if (!/\s/.test(text[i + 1] || "")) starOpenIndex = i;
			continue;
		}
		if (OPENERS.includes(char)) {
			if (char === "<" && !isProbablyAngleTag(i, text)) continue;
			stack.push({
				char,
				index: i
			});
			continue;
		}
		if (CLOSERS.includes(char)) {
			const last = stack[stack.length - 1];
			if (last && BRACKET_MAP[last.char] === char) {
				stack.pop();
				if (options?.keepNarrativeText) {
					charsToRemove.add(last.index);
					charsToRemove.add(i);
				} else rangesToRemove.push([last.index, i]);
			}
		}
	}
	let result = "";
	for (let i = 0; i < text.length; i++) if (options?.keepNarrativeText) {
		if (!charsToRemove.has(i)) result += text[i];
	} else {
		let inRange = false;
		for (const [start, end] of rangesToRemove) if (i >= start && i <= end) {
			inRange = true;
			break;
		}
		if (!inRange) result += text[i];
	}
	return result;
}
function createTtsSegmentStream(tokens, meta, options) {
	const { stream, write, close, error } = createPushStream();
	const pendingSpecials = [];
	const encoder = new TextEncoder();
	const { stream: byteStream, write: writeBytes, close: closeBytes, error: errorBytes } = createPushStream();
	(async () => {
		const reader = tokens.getReader();
		let pendingText = "";
		try {
			while (true) {
				const { value, done } = await reader.read();
				if (done) break;
				if (!value) continue;
				if (value.type === "literal") {
					if (value.value) {
						if (!options?.stripNarrative) {
							writeBytes(encoder.encode(value.value));
							continue;
						}
						pendingText += value.value;
						const stack = [];
						for (let i = 0; i < pendingText.length; i++) {
							const char = pendingText[i];
							if (OPENERS.includes(char)) {
								if (char === "<" && !isProbablyAngleTag(i, pendingText)) continue;
								stack.push(char);
							} else if (CLOSERS.includes(char)) {
								const lastOpen = stack[stack.length - 1];
								if (lastOpen && BRACKET_MAP[lastOpen] === char) stack.pop();
							}
						}
						const bracketsUnclosed = stack.length > 0;
						const starMatch = pendingText.match(/\*([^*]*)$/);
						const starsUnclosed = (pendingText.match(/\*/g) || []).length % 2 !== 0 && starMatch !== null && !starMatch[1].startsWith(" ");
						const hasUnclosed = bracketsUnclosed || starsUnclosed;
						const hasNarrativeUnclosed = stack.some((char) => [
							"[",
							"【",
							"<",
							"（"
						].includes(char));
						const fallbackLimit = options?.stripNarrative && hasNarrativeUnclosed ? 800 : 200;
						if (!hasUnclosed || pendingText.length > fallbackLimit) {
							const textToEmit = processNarrative(pendingText, options);
							writeBytes(encoder.encode(textToEmit));
							pendingText = "";
						}
					}
				} else if (value.type === "special" || value.type === "flush") {
					if (pendingText) {
						const textToEmit = processNarrative(pendingText, options);
						writeBytes(encoder.encode(textToEmit));
						pendingText = "";
					}
					if (value.type === "special") {
						pendingSpecials.push(value.value ?? "");
						writeBytes(encoder.encode("⁣"));
					} else if (value.type === "flush") writeBytes(encoder.encode("​"));
				}
			}
			if (pendingText) {
				let finalPunt = pendingText;
				if (options?.stripNarrative) finalPunt = processNarrative(finalPunt, options);
				writeBytes(encoder.encode(finalPunt));
			}
			closeBytes();
		} catch (err) {
			errorBytes(err);
		} finally {
			reader.releaseLock();
		}
	})();
	(async () => {
		try {
			await chunkEmitter(byteStream.getReader(), pendingSpecials, options, async (chunk) => {
				write({
					turnId: meta.turnId,
					streamId: meta.streamId,
					intentId: meta.intentId,
					segmentId: `${meta.streamId}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
					text: chunk.chunk,
					special: chunk.special,
					reason: chunk.reason,
					createdAt: Date.now()
				});
			});
			close();
		} catch (err) {
			error(err);
		}
	})();
	return stream;
}
//#endregion
//#region src/timeline.ts
function createDefaultClock() {
	return {
		now: () => Date.now(),
		sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms))
	};
}
function createId$1(prefix) {
	return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
/**
* Creates a thin named-track timeline for ordering async media/control work.
*
* Use when:
* - Multiple async queues need a shared ordering clock
* - Items on the same named track must execute serially
*
* Expects:
* - Item handlers own their business side effects
* - Cancellation is cooperative through the provided signal
*
* Returns:
* - A scheduler that serializes each track while allowing different tracks to run concurrently
*/
function createTimeline(options) {
	const clock = options?.clock ?? createDefaultClock();
	const trackTails = /* @__PURE__ */ new Map();
	function enqueue(item) {
		const id = item.id ?? createId$1("timeline");
		const controller = new AbortController();
		const previous = trackTails.get(item.track);
		const run = async () => {
			if (controller.signal.aborted) return;
			await item.run({
				clock,
				signal: controller.signal
			});
		};
		const done = previous ? previous.catch(() => void 0).then(run) : run();
		trackTails.set(item.track, done.catch(() => void 0));
		return {
			id,
			track: item.track,
			done,
			cancel(reason) {
				controller.abort(reason);
			}
		};
	}
	async function flush(track) {
		if (track) {
			await trackTails.get(track);
			return;
		}
		await Promise.all(trackTails.values());
	}
	return {
		clock,
		enqueue,
		flush
	};
}
//#endregion
//#region src/speech-pipeline.ts
function createId(prefix) {
	return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
function createSpeechPipeline(options) {
	const logger = options.logger ?? console;
	const priorityResolver = options.priority ?? createPriorityResolver();
	const segmenter = options.segmenter ?? createTtsSegmentStream;
	const ttsMaxConcurrent = Math.max(1, options.ttsMaxConcurrent ?? 4);
	const context = createContext();
	const timeline = createTimeline();
	const intents = /* @__PURE__ */ new Map();
	const pending = [];
	let activeIntent = null;
	const playbackWaiters = /* @__PURE__ */ new Map();
	function resolvePlayback(itemId) {
		const resolve = playbackWaiters.get(itemId);
		if (!resolve) return;
		playbackWaiters.delete(itemId);
		resolve();
	}
	options.playback.onStart((event) => context.emit(speechPipelineEventMap.onPlaybackStart, event));
	options.playback.onEnd((event) => {
		context.emit(speechPipelineEventMap.onPlaybackEnd, event);
		resolvePlayback(event.item.id);
	});
	options.playback.onInterrupt((event) => {
		context.emit(speechPipelineEventMap.onPlaybackInterrupt, event);
		resolvePlayback(event.item.id);
	});
	options.playback.onReject((event) => {
		context.emit(speechPipelineEventMap.onPlaybackReject, event);
		resolvePlayback(event.item.id);
	});
	function waitForPlayback(item) {
		return new Promise((resolve) => {
			playbackWaiters.set(item.id, resolve);
			options.playback.schedule(item);
		});
	}
	function enqueueIntent(intent) {
		pending.push(intent);
	}
	function pickNextIntent() {
		if (pending.length === 0) return null;
		pending.sort((a, b) => b.priority - a.priority || a.createdAt - b.createdAt);
		return pending.shift() ?? null;
	}
	async function runIntent(intent) {
		activeIntent = intent;
		context.emit(speechPipelineEventMap.onIntentStart, intent.intentId);
		if (intent.turnId) context.emit(speechPipelineEventMap.onTurnStart, intent.turnId);
		const tokenStream = intent.stream;
		const segmentStream = segmenter(tokenStream, {
			streamId: intent.streamId,
			intentId: intent.intentId,
			turnId: intent.turnId
		});
		const completedRequests = /* @__PURE__ */ new Map();
		const inFlightTasks = /* @__PURE__ */ new Set();
		let nextRequestSequence = 0;
		let nextSequenceToSchedule = 0;
		function enqueueSpecial(segment) {
			timeline.enqueue({
				id: `special:${segment.segmentId}`,
				track: "speech",
				run() {
					if (intent.canceled || intent.controller.signal.aborted) return;
					context.emit(speechPipelineEventMap.onSpecial, segment);
				}
			});
		}
		function enqueuePlayback(item) {
			timeline.enqueue({
				id: `playback:${item.id}`,
				track: "speech",
				async run() {
					if (intent.canceled || intent.controller.signal.aborted) return;
					await waitForPlayback(item);
					if (intent.canceled || intent.controller.signal.aborted) return;
					if (item.special) context.emit(speechPipelineEventMap.onSpecial, {
						turnId: item.turnId,
						streamId: item.streamId,
						intentId: item.intentId,
						segmentId: item.segmentId,
						text: item.text,
						special: item.special,
						reason: "special",
						createdAt: item.createdAt
					});
				}
			});
		}
		function scheduleCompletedRequests() {
			while (completedRequests.has(nextSequenceToSchedule)) {
				const completedRequest = completedRequests.get(nextSequenceToSchedule) ?? null;
				completedRequests.delete(nextSequenceToSchedule);
				if (completedRequest) enqueuePlayback({
					id: createId("playback"),
					turnId: completedRequest.turnId,
					streamId: completedRequest.streamId,
					intentId: completedRequest.intentId,
					segmentId: completedRequest.segmentId,
					sequence: completedRequest.sequence,
					ownerId: intent.ownerId,
					priority: intent.priority,
					text: completedRequest.text,
					special: completedRequest.special,
					audio: completedRequest.audio,
					createdAt: Date.now()
				});
				nextSequenceToSchedule += 1;
			}
		}
		function createTtsTask(request) {
			const task = (async () => {
				let audio = null;
				try {
					audio = await options.tts(request, intent.controller.signal);
				} catch (err) {
					logger.warn("TTS generation failed:", err);
					if (intent.controller.signal.aborted) return;
				}
				if (intent.controller.signal.aborted) {
					completedRequests.set(request.sequence, null);
					scheduleCompletedRequests();
					return;
				}
				if (!audio) {
					completedRequests.set(request.sequence, null);
					scheduleCompletedRequests();
					return;
				}
				const ttsResult = {
					turnId: request.turnId,
					streamId: request.streamId,
					intentId: request.intentId,
					segmentId: request.segmentId,
					sequence: request.sequence,
					text: request.text,
					special: request.special,
					audio,
					createdAt: Date.now()
				};
				context.emit(speechPipelineEventMap.onTtsResult, ttsResult);
				completedRequests.set(request.sequence, ttsResult);
				scheduleCompletedRequests();
			})().finally(() => {
				inFlightTasks.delete(task);
			});
			inFlightTasks.add(task);
			return task;
		}
		try {
			const reader = segmentStream.getReader();
			while (true) {
				while (!intent.controller.signal.aborted && inFlightTasks.size >= ttsMaxConcurrent) await Promise.race(inFlightTasks);
				const { value, done } = await reader.read();
				if (done) break;
				if (!value) continue;
				if (intent.canceled || intent.controller.signal.aborted) {
					await reader.cancel();
					break;
				}
				context.emit(speechPipelineEventMap.onSegment, value);
				if (value.text === "" && value.special) {
					enqueueSpecial(value);
					continue;
				}
				const request = {
					turnId: value.turnId,
					streamId: value.streamId,
					intentId: value.intentId,
					segmentId: value.segmentId,
					sequence: nextRequestSequence++,
					text: value.text,
					special: value.special,
					priority: intent.priority,
					createdAt: Date.now()
				};
				context.emit(speechPipelineEventMap.onTtsRequest, request);
				createTtsTask(request);
			}
			await Promise.allSettled(inFlightTasks);
			scheduleCompletedRequests();
			await timeline.flush("speech");
			reader.releaseLock();
		} catch (err) {
			logger.warn("Speech pipeline intent failed:", err);
		} finally {
			if (intent.canceled) {
				context.emit(speechPipelineEventMap.onIntentCancel, {
					intentId: intent.intentId,
					reason: intent.controller.signal.reason
				});
				if (intent.turnId) context.emit(speechPipelineEventMap.onTurnCancel, {
					turnId: intent.turnId,
					reason: intent.controller.signal.reason
				});
			} else {
				context.emit(speechPipelineEventMap.onIntentEnd, intent.intentId);
				if (intent.turnId) context.emit(speechPipelineEventMap.onTurnEnd, intent.turnId);
			}
			intents.delete(intent.intentId);
			if (activeIntent?.intentId === intent.intentId) activeIntent = null;
			if (!activeIntent) {
				const next = pickNextIntent();
				if (next) runIntent(next);
			}
		}
	}
	function openIntent(optionsInput) {
		const intentId = optionsInput?.intentId ?? createId("intent");
		const turnId = optionsInput?.turnId;
		const streamId = optionsInput?.streamId ?? createId("stream");
		const priority = priorityResolver.resolve(optionsInput?.priority);
		const behavior = optionsInput?.behavior ?? "queue";
		const ownerId = optionsInput?.ownerId;
		const controller = new AbortController();
		const { stream, write, close } = createPushStream();
		let sequence = 0;
		const intent = {
			turnId,
			intentId,
			streamId,
			priority,
			ownerId,
			behavior,
			createdAt: Date.now(),
			controller,
			stream,
			closeStream: close,
			canceled: false
		};
		intents.set(intentId, intent);
		const handle = {
			turnId,
			intentId,
			streamId,
			priority,
			ownerId,
			stream,
			writeLiteral(text) {
				if (intent.canceled) return;
				write({
					type: "literal",
					value: text,
					turnId,
					streamId,
					intentId,
					sequence: sequence++,
					createdAt: Date.now()
				});
			},
			writeSpecial(special) {
				if (intent.canceled) return;
				write({
					type: "special",
					value: special,
					turnId,
					streamId,
					intentId,
					sequence: sequence++,
					createdAt: Date.now()
				});
			},
			writeFlush() {
				if (intent.canceled) return;
				write({
					type: "flush",
					turnId,
					streamId,
					intentId,
					sequence: sequence++,
					createdAt: Date.now()
				});
			},
			end() {
				close();
			},
			cancel(reason) {
				cancelIntent(intentId, reason);
			}
		};
		if (!activeIntent) {
			runIntent(intent);
			return handle;
		}
		if (behavior === "replace") {
			cancelIntent(activeIntent.intentId, "replace");
			runIntent(intent);
			return handle;
		}
		if (behavior === "interrupt" && intent.priority >= activeIntent.priority) {
			cancelIntent(activeIntent.intentId, "interrupt");
			runIntent(intent);
			return handle;
		}
		enqueueIntent(intent);
		return handle;
	}
	function cancelIntent(intentId, reason) {
		const intent = intents.get(intentId);
		if (!intent) return;
		intent.canceled = true;
		intent.controller.abort(reason ?? "canceled");
		intent.closeStream();
		if (activeIntent?.intentId === intentId) {
			options.playback.stopByIntent(intentId, reason ?? "canceled");
			return;
		}
		const index = pending.findIndex((item) => item.intentId === intentId);
		if (index >= 0) pending.splice(index, 1);
	}
	function interrupt(reason) {
		if (activeIntent) cancelIntent(activeIntent.intentId, reason);
	}
	function stopAll(reason) {
		for (const intent of intents.values()) {
			intent.canceled = true;
			intent.controller.abort(reason);
			intent.closeStream();
		}
		pending.length = 0;
		intents.clear();
		activeIntent = null;
		options.playback.stopAll(reason);
	}
	return {
		openIntent,
		cancelIntent,
		interrupt,
		stopAll,
		on(event, listener) {
			return context.on(speechPipelineEventMap[event], (payload) => {
				listener(payload?.body ?? payload);
			});
		}
	};
}
//#endregion
export { TTS_FLUSH_INSTRUCTION, TTS_SPECIAL_TOKEN, chunkEmitter, chunkTtsInput, comparePriority, createPlaybackManager, createPriorityResolver, createPushStream, createSpeechPipeline, createStreamingControlParser, createTimeline, createTtsSegmentStream, isProbablyAngleTag, normalizeActPayload, processNarrative, readStream, speechIntentCancelEvent, speechIntentEndEvent, speechIntentStartEvent, speechPipelineEventMap, speechPlaybackEndEvent, speechPlaybackInterruptEvent, speechPlaybackRejectEvent, speechPlaybackStartEvent, speechSegmentEvent, speechSpecialEvent, speechTtsRequestEvent, speechTtsResultEvent, speechTurnCancelEvent, speechTurnEndEvent, speechTurnStartEvent };
